import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { request } from 'core/constants'
import Table from 'components/lib/Table'
import Button from 'components/lib/Button'
import Dropdown from 'components/lib/Dropdown'
import Icon from 'components/lib/Icon'
import ExtraBillingHomeHeader from './components/ExtraBillingHomeHeader'
import CreateExtraBillingModal from './components/CreateExtraBillingModal'
import ExtraBillingCreatedModal from './components/ExtraBillingCreatedModal'
import ResendExtraBillingModal from './components/ResendExtraBillingModal'
import CancelExtraBillingModal from './components/CancelExtraBillingModal'
import moment from 'moment'

const IconButton = styled.button`
  border: none;
  border-radius: 4px;
  display: flex;
  align-items: center;
  height: 100%;
  cursor: pointer;
  font-size: 1rem;
`

const DropdownItem = styled(Dropdown.Item)`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  align-items: center;
  font-size: 0.875rem;
  line-height: 1.5;
  padding: 0.375rem 1rem;
  cursor: pointer;
  color: hsl(0deg, 0%, 29%);
`

const ExtraBilling = (props) => {
  const companyId = props.companyId
  const proposalId = props.process.proposal.id
  const clients = props.process.proposal.clients

  const pageWidth = document.body.offsetWidth

  const [items, setItems] = useState([])
  const [showCreateExtraBillingModal, setShowCreateExtraBillingModal] =
    useState(false)
  const [showExtraBillingCreatedModal, setShowExtraBillingCreatedModal] =
    useState(false)
  const [extraBillingToResend, setExtraBillingToResend] = useState(null)
  const [extraBillingToCancel, setExtraBillingToCancel] = useState(null)

  useEffect(() => {
    const fetchData = async () => {
      if (companyId && proposalId) {
        const response = await request.get(
          `company/${companyId}/proposal/${proposalId}/extra-billing`,
        )

        setItems(response.data)
      }
    }

    fetchData()
  }, [companyId, proposalId])

  return (
    <div>
      <ExtraBillingHomeHeader
        onCreateBillingRequest={() => setShowCreateExtraBillingModal(true)}
      />

      {items.length > 0 && (
        <div>
          <Table
            items={items}
            cells={[
              {
                name: 'Data de criação',
                path: 'created_at',
                align: 'center',
                render: (content, row) => {
                  const dt = row.created_at

                  if (dt == null) {
                    return ''
                  }

                  const format = 'DD/MM/YYYY H:mm:ss'

                  return moment(dt).format(format)
                },
              },
              {
                name: 'Descrição',
                path: 'description',
                align: 'center',
              },
              {
                name: 'Forma de pagamento',
                path: 'payment_form',
                align: 'center',
                render: (content, row) => (
                  <div>{row.payment_form == 'TICKET' ? 'Boleto' : 'Pix'}</div>
                ),
              },
              {
                name: 'Valor',
                path: 'price',
                align: 'center',
                render: (content, row) => (
                  <div>{`R$ ${row.price.toFixed(2).replace('.', ',')}`}</div>
                ),
              },
              {
                name: 'Status',
                path: 'status',
                align: 'center',
                render: (content, row) => (
                  <div>
                    {row.status === 'WAITING' && 'Aguardando pagamento'}
                    {row.status === 'PAID' && 'Pago'}
                    {row.status === 'EXPIRED' && 'Expirado'}
                    {row.status === 'CANCELED' && 'Cancelado'}
                  </div>
                ),
              },
              {
                name: 'Ações',
                align: 'center',
                render: (content, row) =>
                  row.status !== 'WAITING' ? (
                    <p></p>
                  ) : pageWidth < 1024 ? (
                    <div>
                      <Button
                        hasThemeColor
                        title='Reenviar'
                        style={{ marginRight: '5px' }}
                        onClick={() => {
                          setExtraBillingToResend(row)
                        }}
                      >
                        <Icon name='fa fa-redo' />
                      </Button>
                      <Button
                        hasThemeColor
                        title='Cancelar'
                        style={{ marginRight: '5px' }}
                        onClick={() => {
                          setExtraBillingToCancel(row)
                        }}
                      >
                        <Icon name='fas fa-times' />
                      </Button>
                    </div>
                  ) : (
                    <Dropdown
                      isRight
                      keepOpen
                      trigger={
                        <IconButton className='has-content-theme-color'>
                          <Icon name='fa fa-ellipsis-v' />
                        </IconButton>
                      }
                    >
                      <DropdownItem
                        onClick={() => {
                          setExtraBillingToResend(row)
                        }}
                      >
                        <Icon name='fa fa-redo' />
                        Reenviar cobrança
                      </DropdownItem>
                      <DropdownItem
                        onClick={() => {
                          setExtraBillingToCancel(row)
                        }}
                      >
                        <Icon name='fa fa-times' />
                        Cancelar cobrança
                      </DropdownItem>
                    </Dropdown>
                  ),
              },
            ]}
          />
        </div>
      )}

      <CreateExtraBillingModal
        isOpen={showCreateExtraBillingModal}
        onClose={() => setShowCreateExtraBillingModal(false)}
        companyId={companyId}
        proposalId={proposalId}
        clients={clients}
        onCreated={(newItem) => {
          setItems((prev) => [...prev, newItem])
          setShowCreateExtraBillingModal(false)
          setShowExtraBillingCreatedModal(true)
        }}
      />

      <ExtraBillingCreatedModal
        isOpen={showExtraBillingCreatedModal}
        onClose={() => setShowExtraBillingCreatedModal(false)}
      />

      <ResendExtraBillingModal
        isOpen={!!extraBillingToResend}
        onClose={() => setExtraBillingToResend(null)}
        onResent={() => {
          setExtraBillingToResend(null)
        }}
        extraBilling={extraBillingToResend}
        companyId={companyId}
        proposalId={proposalId}
      />

      <CancelExtraBillingModal
        isOpen={!!extraBillingToCancel}
        onClose={() => setExtraBillingToCancel(null)}
        onCanceled={() => {
          setExtraBillingToCancel(null)
        }}
        extraBilling={extraBillingToCancel}
        companyId={companyId}
        proposalId={proposalId}
      />
    </div>
  )
}

ExtraBilling.propTypes = {
  process: PropTypes.object.isRequired,
}

export default ExtraBilling
